import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Mail, TrendingUp, Target, Tag, Globe, Filter,
  UserPlus, ArrowUpRight, Loader2, ChevronRight, BarChart3, 
  Zap, Search, Eye, MousePointer, ShoppingCart, RefreshCw,
  Plus, X, Check, Edit2, Trash2, User, Calendar, Activity,
  Download, Clock, Bell, Phone, Building, BookOpen, FileText,
  ExternalLink, ChevronDown, Layers, Link2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api from '../../lib/api';
import { toast } from 'sonner';
import AdminAutomations from './AdminAutomations';
import AdminFunnels from './AdminFunnels';

const AdminMarketing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [activeWaitlistTab, setActiveWaitlistTab] = useState('campus');
  
  // Data states
  const [dashboardData, setDashboardData] = useState(null);
  const [webStats, setWebStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [tags, setTags] = useState([]);
  const [marketingStats, setMarketingStats] = useState(null);
  const [attributionStats, setAttributionStats] = useState(null);
  const [crmHealth, setCrmHealth] = useState(null);
  
  // Waitlist states
  const [campusWaitlist, setCampusWaitlist] = useState([]);
  const [campusStats, setCampusStats] = useState(null);
  const [publicacionesWaitlist, setPublicacionesWaitlist] = useState([]);
  const [publicacionesStats, setPublicacionesStats] = useState(null);
  const [viajesSolicitudes, setViajesSolicitudes] = useState([]);
  const [limitesSolicitudes, setLimitesSolicitudes] = useState([]);
  
  // Filters and pagination
  const [contactSearch, setContactSearch] = useState('');
  const [contactFilter, setContactFilter] = useState('all');
  const [selectedContact, setSelectedContact] = useState(null);
  const [waitlistSearch, setWaitlistSearch] = useState('');
  const [lastError, setLastError] = useState('');
  const errorCooldownRef = useRef({});
  const [dedupeLoading, setDedupeLoading] = useState(false);
  const [dedupeReport, setDedupeReport] = useState(null);
  
  // Modals
  const [showTagModal, setShowTagModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [newTag, setNewTag] = useState({ name: '', color: '#6B7280', description: '' });

  const notifyApiError = (key, message, error) => {
    console.error(message, error);
    setLastError(message);
    const now = Date.now();
    const lastTs = errorCooldownRef.current[key] || 0;
    if (now - lastTs > 3500) {
      toast.error(message);
      errorCooldownRef.current[key] = now;
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'contacts') fetchContacts();
    if (activeTab === 'web') fetchWebStats();
    if (activeTab === 'tags') fetchTags();
    if (activeTab === 'waitlists') {
      if (activeWaitlistTab === 'campus') fetchCampusWaitlist();
      if (activeWaitlistTab === 'publicaciones') fetchPublicacionesWaitlist();
      if (activeWaitlistTab === 'viajes') fetchViajesSolicitudes();
      if (activeWaitlistTab === 'limites') fetchLimitesSolicitudes();
    }
    if (activeTab === 'attribution') fetchAttributionStats();
  }, [activeTab, contactFilter, activeWaitlistTab]);

  const fetchInitialData = async () => {
    try {
      const [ceoRes, statsRes, crmRes] = await Promise.all([
        api.get('/admin/ceo-dashboard'),
        api.get('/marketing/stats'),
        api.get('/crm/admin/validate')
      ]);
      setDashboardData(ceoRes.data.motor_crecimiento);
      setMarketingStats(statsRes.data);
      setCrmHealth(crmRes.data);
      
      // Also fetch waitlist counters for the dashboard cards
      fetchWaitlistCounters();
    } catch (error) {
      notifyApiError('initial', 'Error al cargar el panel de marketing', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchWaitlistCounters = async () => {
    try {
      const [campusRes, pubRes, viajesRes, limitesRes] = await Promise.all([
        api.get('/admin/campus/waitlist/stats').catch(() => ({ data: { total: 0 } })),
        api.get('/admin/publicaciones/waitlist/stats').catch(() => ({ data: { total: 0 } })),
        api.get('/admin/viajes/solicitudes').catch(() => ({ data: [] })),
        api.get('/admin/curso-limites/solicitudes').catch(() => ({ data: [] }))
      ]);
      setCampusStats(campusRes.data);
      setPublicacionesStats(pubRes.data);
      setViajesSolicitudes(viajesRes.data);
      setLimitesSolicitudes(limitesRes.data);
    } catch (error) {
      console.error('Error fetching waitlist counters:', error);
    }
  };

  const fetchCrmHealth = async () => {
    try {
      const res = await api.get('/crm/admin/validate');
      setCrmHealth(res.data);
    } catch (error) {
      notifyApiError('crm_health', 'Error al cargar salud del CRM', error);
    }
  };

  const fetchContacts = async () => {
    try {
      let url = '/marketing/contacts?limit=100';
      if (contactFilter !== 'all') url += `&lifecycle_stage=${contactFilter}`;
      if (contactSearch) url += `&search=${encodeURIComponent(contactSearch)}`;
      const res = await api.get(url);
      setContacts(res.data);
    } catch (error) {
      notifyApiError('contacts', 'Error al cargar contactos', error);
    }
  };

  const fetchWebStats = async () => {
    try {
      const res = await api.get('/marketing/stats/web?days=30');
      setWebStats(res.data);
    } catch (error) {
      notifyApiError('web_stats', 'Error al cargar estadísticas web', error);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await api.get('/marketing/tags');
      setTags(res.data);
    } catch (error) {
      notifyApiError('tags', 'Error al cargar audiencias/tags', error);
    }
  };

  const fetchAttributionStats = async () => {
    try {
      const [webRes, attrRes] = await Promise.all([
        api.get('/marketing/stats/web?days=30'),
        api.get('/marketing/stats/attribution')
      ]);
      setWebStats(webRes.data);
      setAttributionStats(attrRes.data);
    } catch (error) {
      notifyApiError('attribution', 'Error al cargar atribución y orígenes', error);
    }
  };

  // Waitlist functions
  const fetchCampusWaitlist = async () => {
    try {
      const [listRes, statsRes] = await Promise.all([
        api.get('/admin/campus/waitlist'),
        api.get('/admin/campus/waitlist/stats')
      ]);
      setCampusWaitlist(listRes.data);
      setCampusStats(statsRes.data);
    } catch (error) {
      notifyApiError('wl_campus', 'Error al cargar lista de espera de Campus', error);
    }
  };

  const fetchPublicacionesWaitlist = async () => {
    try {
      const [listRes, statsRes] = await Promise.all([
        api.get('/admin/publicaciones/waitlist'),
        api.get('/admin/publicaciones/waitlist/stats')
      ]);
      setPublicacionesWaitlist(listRes.data);
      setPublicacionesStats(statsRes.data);
    } catch (error) {
      notifyApiError('wl_publicaciones', 'Error al cargar lista de espera de Publicaciones', error);
    }
  };

  const fetchViajesSolicitudes = async () => {
    try {
      const response = await api.get('/admin/viajes/solicitudes');
      setViajesSolicitudes(response.data);
    } catch (error) {
      notifyApiError('wl_viajes', 'Error al cargar solicitudes de Viajes', error);
    }
  };

  const updateViajeStatus = async (entryId, newStatus) => {
    try {
      await api.put(`/admin/viajes/solicitudes/${entryId}/status`, { status: newStatus });
      toast.success('Estado actualizado');
      fetchViajesSolicitudes();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const deleteViajeSolicitud = async (entryId) => {
    if (!window.confirm('¿Eliminar esta solicitud?')) return;
    try {
      await api.delete(`/admin/viajes/solicitudes/${entryId}`);
      toast.success('Solicitud eliminada');
      fetchViajesSolicitudes();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Límites Solicitudes functions
  const fetchLimitesSolicitudes = async () => {
    try {
      const response = await api.get('/admin/curso-limites/solicitudes');
      setLimitesSolicitudes(response.data);
    } catch (error) {
      notifyApiError('wl_limites', 'Error al cargar solicitudes de Límites', error);
    }
  };

  const updateLimitesStatus = async (entryId, newStatus) => {
    try {
      await api.put(`/admin/curso-limites/solicitudes/${entryId}/status`, { status: newStatus });
      toast.success('Estado actualizado');
      fetchLimitesSolicitudes();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const deleteLimitesSolicitud = async (entryId) => {
    if (!window.confirm('¿Eliminar esta solicitud?')) return;
    try {
      await api.delete(`/admin/curso-limites/solicitudes/${entryId}`);
      toast.success('Solicitud eliminada');
      fetchLimitesSolicitudes();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const markWaitlistNotified = async (type, entryId) => {
    try {
      const endpoint = type === 'campus' 
        ? `/admin/campus/waitlist/${entryId}/notify`
        : `/admin/publicaciones/waitlist/${entryId}/notify`;
      await api.put(endpoint);
      toast.success('Marcado como notificado');
      if (type === 'campus') fetchCampusWaitlist();
      else fetchPublicacionesWaitlist();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const deleteWaitlistEntry = async (type, entryId) => {
    if (!window.confirm('¿Eliminar esta entrada?')) return;
    try {
      const endpoint = type === 'campus' 
        ? `/admin/campus/waitlist/${entryId}`
        : `/admin/publicaciones/waitlist/${entryId}`;
      await api.delete(endpoint);
      toast.success('Entrada eliminada');
      if (type === 'campus') fetchCampusWaitlist();
      else fetchPublicacionesWaitlist();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const exportWaitlist = async (type) => {
    try {
      const endpoint = type === 'campus' 
        ? '/admin/campus/waitlist/export'
        : '/admin/publicaciones/waitlist/export';
      const res = await api.get(endpoint);
      
      const csvContent = res.data.data.map(row => 
        `${row.email},${row.name || ''},${row.phone || ''},${row.subscribed_at || ''}`
      ).join('\n');
      
      const blob = new Blob([`email,nombre,telefono,fecha\n${csvContent}`], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-waitlist-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast.success('CSV exportado');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const createTag = async () => {
    if (!newTag.name.trim()) {
      toast.error('El nombre del tag es obligatorio');
      return;
    }
    try {
      await api.post('/marketing/tags', newTag);
      toast.success('Tag creado');
      setShowTagModal(false);
      setNewTag({ name: '', color: '#6B7280', description: '' });
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear tag');
    }
  };

  const deleteTag = async (tagId) => {
    if (!window.confirm('¿Eliminar este tag?')) return;
    try {
      await api.delete(`/marketing/tags/${tagId}`);
      toast.success('Tag eliminado');
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const viewContact = async (contactId) => {
    try {
      const res = await api.get(`/marketing/contacts/${contactId}`);
      setSelectedContact(res.data);
      setShowContactModal(true);
    } catch (error) {
      toast.error('Error al cargar contacto');
    }
  };

  const runDeduplication = async (dryRun = true) => {
    setDedupeLoading(true);
    try {
      const response = await api.post(`/crm/admin/deduplicate?dry_run=${dryRun ? 'true' : 'false'}`);
      setDedupeReport(response.data);
      if (dryRun) {
        toast.success('Análisis de duplicados completado');
      } else {
        toast.success(`Deduplicación completada: ${response.data.total_merged || 0} fusiones`);
        fetchContacts();
        fetchInitialData();
        fetchCrmHealth();
      }
    } catch (error) {
      notifyApiError('dedupe', error?.response?.data?.detail || 'Error al ejecutar deduplicación', error);
    } finally {
      setDedupeLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-ES');
  };

  const tabs = [
    { id: 'dashboard', label: 'Panel', icon: BarChart3 },
    { id: 'contacts', label: 'Suscriptores', icon: Users },
    { id: 'tags', label: 'Audiencias', icon: Tag },
    { id: 'waitlists', label: 'Listas de Espera', icon: Clock },
    { id: 'automations', label: 'Automatizaciones', icon: Zap },
    { id: 'funnels', label: 'Embudos', icon: TrendingUp },
    { id: 'web', label: 'Estadísticas Web', icon: Globe },
    { id: 'attribution', label: 'Orígenes/UTM', icon: Link2 },
  ];

  // Filter waitlist by search
  const filteredCampusWaitlist = campusWaitlist.filter(entry => 
    !waitlistSearch || 
    entry.email?.toLowerCase().includes(waitlistSearch.toLowerCase()) ||
    entry.name?.toLowerCase().includes(waitlistSearch.toLowerCase())
  );

  const filteredPublicacionesWaitlist = publicacionesWaitlist.filter(entry => 
    !waitlistSearch || 
    entry.email?.toLowerCase().includes(waitlistSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 lg:p-8 min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-marketing">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900" data-testid="marketing-title">
          Marketing & CRM
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Gestión de contactos, audiencias, tracking y conversiones
        </p>
        {lastError && (
          <div className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
            <p className="text-xs text-rose-700">{lastError}</p>
            <button
              className="text-rose-500 hover:text-rose-700"
              onClick={() => setLastError('')}
              title="Cerrar aviso"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200 pb-2 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-[#c9a227] text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ========== DASHBOARD TAB ========== */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6" data-testid="dashboard-tab">
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-400 uppercase">Contactos</span>
                <Users className="w-4 h-4 text-blue-500" />
              </div>
              <p className="text-2xl font-semibold text-slate-900">{marketingStats?.contacts?.total || 0}</p>
              <p className="text-xs text-slate-400 mt-1">{marketingStats?.contacts?.active || 0} activos</p>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-400 uppercase">Suscriptores</span>
                <Mail className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="text-2xl font-semibold text-slate-900">{marketingStats?.contacts?.subscribers || 0}</p>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-400 uppercase">Leads</span>
                <UserPlus className="w-4 h-4 text-purple-500" />
              </div>
              <p className="text-2xl font-semibold text-slate-900">{marketingStats?.contacts?.leads || 0}</p>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-400 uppercase">Clientes</span>
                <ShoppingCart className="w-4 h-4 text-amber-500" />
              </div>
              <p className="text-2xl font-semibold text-emerald-600">{marketingStats?.contacts?.customers || 0}</p>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-400 uppercase">Conversión</span>
                <Target className="w-4 h-4 text-red-500" />
              </div>
              <p className={`text-2xl font-semibold ${marketingStats?.conversion_rate >= 2 ? 'text-emerald-600' : 'text-amber-600'}`}>
                {marketingStats?.conversion_rate || 0}%
              </p>
            </div>
          </div>

          {/* CRM Health */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-semibold text-slate-900">Salud CRM</h2>
                <p className="text-xs text-slate-500">Integridad técnica de contactos y eventos</p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchCrmHealth}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-[11px] uppercase text-slate-500">Duplicados</p>
                <p className={`text-lg font-semibold ${(crmHealth?.duplicates || 0) > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {crmHealth?.duplicates ?? '-'}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-[11px] uppercase text-slate-500">Sin email_norm</p>
                <p className={`text-lg font-semibold ${(crmHealth?.normalization?.missing_email_normalized || 0) > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {crmHealth?.normalization?.missing_email_normalized ?? '-'}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-[11px] uppercase text-slate-500">Eventos sin contacto</p>
                <p className={`text-lg font-semibold ${(crmHealth?.events_integrity?.without_contact_id || 0) > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {crmHealth?.events_integrity?.without_contact_id ?? '-'}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-[11px] uppercase text-slate-500">Eventos huérfanos</p>
                <p className={`text-lg font-semibold ${(crmHealth?.events_integrity?.orphan_contact_id || 0) > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {crmHealth?.events_integrity?.orphan_contact_id ?? '-'}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-[11px] uppercase text-slate-500">Índice email_norm</p>
                <p className={`text-lg font-semibold ${crmHealth?.indexes?.email_normalized ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {crmHealth?.indexes?.email_normalized ? 'OK' : 'Falta'}
                </p>
              </div>
            </div>
          </div>

          {/* Embudo y Métricas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Embudo */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Embudo de Conversión
              </h2>
              
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Suscriptores</span>
                    <span className="font-medium">{marketingStats?.contacts?.subscribers || 0}</span>
                  </div>
                  <div className="h-3 bg-blue-100 rounded-full">
                    <div className="h-full bg-blue-500 rounded-full" style={{ width: '100%' }}></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Leads (interés activo)</span>
                    <span className="font-medium">{marketingStats?.contacts?.leads || 0}</span>
                  </div>
                  <div className="h-3 bg-purple-100 rounded-full">
                    <div 
                      className="h-full bg-purple-500 rounded-full" 
                      style={{ width: `${Math.min(100, ((marketingStats?.contacts?.leads || 0) / Math.max(1, marketingStats?.contacts?.subscribers)) * 100)}%` }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Clientes</span>
                    <span className="font-medium">{marketingStats?.contacts?.customers || 0}</span>
                  </div>
                  <div className="h-3 bg-emerald-100 rounded-full">
                    <div 
                      className="h-full bg-emerald-500 rounded-full" 
                      style={{ width: `${Math.min(100, ((marketingStats?.contacts?.customers || 0) / Math.max(1, marketingStats?.contacts?.subscribers)) * 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-4 text-center">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-400">LTV Promedio</p>
                  <p className="font-semibold text-lg text-emerald-600">{formatCurrency(dashboardData?.ltv)}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-400">CAC</p>
                  <p className="font-semibold text-lg text-slate-900">{formatCurrency(dashboardData?.cac)}</p>
                </div>
              </div>
            </div>

            {/* Top Tags */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5 text-indigo-500" />
                Tags Más Usados
              </h2>
              
              <div className="space-y-3">
                {(marketingStats?.top_tags || []).slice(0, 6).map(tag => (
                  <div key={tag.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: tag.color || '#6B7280' }}
                      />
                      <span className="font-medium text-sm">{tag.name}</span>
                    </div>
                    <span className="text-sm text-slate-500">{tag.contact_count} contactos</span>
                  </div>
                ))}
                {(!marketingStats?.top_tags || marketingStats.top_tags.length === 0) && (
                  <p className="text-sm text-slate-400 text-center py-4">Sin tags todavía</p>
                )}
              </div>
            </div>
          </div>

          {/* Resumen Listas de Espera */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-5 text-white">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  <span className="font-medium">Campus de Interioridad</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-white hover:bg-white/20"
                  onClick={() => { setActiveTab('waitlists'); setActiveWaitlistTab('campus'); }}
                >
                  Ver lista <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <p className="text-3xl font-bold">{campusStats?.total || campusWaitlist.length || 0}</p>
              <p className="text-indigo-100 text-sm">personas en lista de espera</p>
            </div>

            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  <span className="font-medium">Publicaciones</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-white hover:bg-white/20"
                  onClick={() => { setActiveTab('waitlists'); setActiveWaitlistTab('publicaciones'); }}
                >
                  Ver lista <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <p className="text-3xl font-bold">{publicacionesStats?.total || publicacionesWaitlist.length || 0}</p>
              <p className="text-amber-100 text-sm">personas interesadas</p>
            </div>
          </div>

          {/* Viajes Card */}
          <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-5 text-white">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                <span className="font-medium">Viajes Iniciáticos</span>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-white hover:bg-white/20"
                onClick={() => { setActiveTab('waitlists'); setActiveWaitlistTab('viajes'); }}
              >
                Ver solicitudes <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <p className="text-3xl font-bold">{viajesSolicitudes.length || 0}</p>
            <p className="text-teal-100 text-sm">solicitudes de información</p>
          </div>
          
          {/* Límites Card */}
          <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-xl p-5 text-white">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                <span className="font-medium">Curso Límites</span>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-white hover:bg-white/20"
                onClick={() => { setActiveTab('waitlists'); setActiveWaitlistTab('limites'); }}
              >
                Ver interesados <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <p className="text-3xl font-bold">{limitesSolicitudes.length || 0}</p>
            <p className="text-rose-100 text-sm">personas interesadas</p>
          </div>
        </div>
      )}

      {/* ========== CONTACTS TAB ========== */}
      {activeTab === 'contacts' && (
        <div className="space-y-4" data-testid="contacts-tab">
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Deduplicación de contactos (CRM 360)</p>
                <p className="text-xs text-slate-500">Primero ejecuta análisis. Después aplica fusión real si todo cuadra.</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => runDeduplication(true)} disabled={dedupeLoading}>
                  {dedupeLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Analizar duplicados
                </Button>
                <Button className="bg-[#c9a227] hover:bg-[#b89220]" onClick={() => runDeduplication(false)} disabled={dedupeLoading}>
                  Aplicar deduplicación
                </Button>
              </div>
            </div>
            {dedupeReport && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-[11px] uppercase text-slate-500">Email candidatos</p>
                  <p className="text-lg font-semibold text-slate-900">{dedupeReport.email_candidates || 0}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-[11px] uppercase text-slate-500">Tel candidatos</p>
                  <p className="text-lg font-semibold text-slate-900">{dedupeReport.phone_candidates || 0}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-[11px] uppercase text-slate-500">Fusiones email</p>
                  <p className="text-lg font-semibold text-emerald-600">{dedupeReport.email_merged || 0}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-[11px] uppercase text-slate-500">Fusiones tel</p>
                  <p className="text-lg font-semibold text-emerald-600">{dedupeReport.phone_merged || 0}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-[11px] uppercase text-slate-500">Errores</p>
                  <p className="text-lg font-semibold text-rose-600">{(dedupeReport.errors || []).length}</p>
                </div>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              {[
                { value: 'all', label: 'Todos' },
                { value: 'subscriber', label: 'Suscriptores' },
                { value: 'lead', label: 'Leads' },
                { value: 'customer', label: 'Clientes' },
              ].map(f => (
                <button
                  key={f.value}
                  onClick={() => setContactFilter(f.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    contactFilter === f.value
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar email o nombre..."
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchContacts()}
                  className="pl-10"
                />
              </div>
              <Button onClick={fetchContacts} variant="outline" size="icon">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Contacts Table */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Email</th>
                    <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Nombre</th>
                    <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Estado</th>
                    <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Tags</th>
                    <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Score</th>
                    <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Creado</th>
                    <th className="text-right p-4 text-xs font-medium text-slate-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {contacts.map(contact => (
                    <tr key={contact.id} className="hover:bg-slate-50">
                      <td className="p-4">
                        <p className="font-medium text-sm text-slate-900">{contact.email}</p>
                      </td>
                      <td className="p-4 text-sm text-slate-600">
                        {contact.first_name || '-'} {contact.last_name || ''}
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          contact.lifecycle_stage === 'customer' ? 'bg-emerald-100 text-emerald-700' :
                          contact.lifecycle_stage === 'lead' ? 'bg-purple-100 text-purple-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {contact.lifecycle_stage}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-1 flex-wrap">
                          {(contact.tag_names || []).slice(0, 2).map((tag, i) => (
                            <span key={i} className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded">
                              {tag}
                            </span>
                          ))}
                          {(contact.tag_names || []).length > 2 && (
                            <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-500 rounded">
                              +{contact.tag_names.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`font-medium text-sm ${contact.lead_score > 10 ? 'text-emerald-600' : 'text-slate-600'}`}>
                          {contact.lead_score || 0}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-slate-500">
                        {formatDate(contact.created_at)}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => viewContact(contact.id)} title="Vista rápida">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/marketing/contactos/${contact.id}`)}
                            title="Abrir ficha 360"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {contacts.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-slate-400">
                        No se encontraron contactos
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ========== TAGS / AUDIENCIAS TAB ========== */}
      {activeTab === 'tags' && (
        <div className="space-y-6" data-testid="tags-tab">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Audiencias y Segmentos</h2>
              <p className="text-sm text-slate-500">{tags.length} tags disponibles</p>
            </div>
            <Button onClick={() => setShowTagModal(true)} className="bg-[#c9a227] hover:bg-[#b89220]">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo tag
            </Button>
          </div>

          {/* Tags por tipo */}
          <div className="space-y-6">
            {/* System Tags */}
            <div>
              <h3 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Tags del Sistema
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tags.filter(t => t.is_system).map(tag => (
                  <div key={tag.id} className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: tag.color || '#6B7280' }}
                        />
                        <span className="font-medium text-slate-900">{tag.name}</span>
                      </div>
                      <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded">Sistema</span>
                    </div>
                    {tag.description && (
                      <p className="text-xs text-slate-500 mb-2">{tag.description}</p>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">{tag.contact_count || 0} contactos</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Tags */}
            <div>
              <h3 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Tags Personalizados
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tags.filter(t => !t.is_system).map(tag => (
                  <div key={tag.id} className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: tag.color || '#6B7280' }}
                        />
                        <span className="font-medium text-slate-900">{tag.name}</span>
                      </div>
                      <button
                        onClick={() => deleteTag(tag.id)}
                        className="p-1 hover:bg-red-50 rounded text-slate-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {tag.description && (
                      <p className="text-xs text-slate-500 mb-2">{tag.description}</p>
                    )}
                    <span className="text-sm text-slate-600">{tag.contact_count || 0} contactos</span>
                  </div>
                ))}
                {tags.filter(t => !t.is_system).length === 0 && (
                  <div className="col-span-3 p-8 text-center text-slate-400 bg-slate-50 rounded-xl">
                    No hay tags personalizados. Crea uno para segmentar tu audiencia.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Lead Scoring Info */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100">
            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-500" />
              Lead Scoring
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              El lead scoring se calcula automáticamente basándose en las acciones del contacto:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="bg-white/80 rounded-lg p-3">
                <p className="font-medium text-purple-700">+5 puntos</p>
                <p className="text-slate-500">Descarga ebook</p>
              </div>
              <div className="bg-white/80 rounded-lg p-3">
                <p className="font-medium text-purple-700">+10 puntos</p>
                <p className="text-slate-500">Visita landing formación</p>
              </div>
              <div className="bg-white/80 rounded-lg p-3">
                <p className="font-medium text-purple-700">+20 puntos</p>
                <p className="text-slate-500">Inicia checkout</p>
              </div>
              <div className="bg-white/80 rounded-lg p-3">
                <p className="font-medium text-emerald-700">+50 puntos</p>
                <p className="text-slate-500">Compra completada</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== WAITLISTS TAB ========== */}
      {activeTab === 'waitlists' && (
        <div className="space-y-6" data-testid="waitlists-tab">
          {/* Sub-tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveWaitlistTab('campus')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeWaitlistTab === 'campus'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Building className="w-4 h-4" />
              Campus de Interioridad
              {campusStats && <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">{campusStats.total}</span>}
            </button>
            <button
              onClick={() => setActiveWaitlistTab('publicaciones')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeWaitlistTab === 'publicaciones'
                  ? 'bg-amber-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              Publicaciones
              {publicacionesStats && <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">{publicacionesStats.total}</span>}
            </button>
            <button
              onClick={() => setActiveWaitlistTab('viajes')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeWaitlistTab === 'viajes'
                  ? 'bg-teal-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Globe className="w-4 h-4" />
              Viajes
              {viajesSolicitudes.length > 0 && <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">{viajesSolicitudes.length}</span>}
            </button>
            <button
              onClick={() => setActiveWaitlistTab('limites')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeWaitlistTab === 'limites'
                  ? 'bg-rose-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Target className="w-4 h-4" />
              Límites
              {limitesSolicitudes.length > 0 && <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">{limitesSolicitudes.length}</span>}
            </button>
          </div>

          {/* Campus Waitlist */}
          {activeWaitlistTab === 'campus' && (
            <div className="space-y-4">
              {/* Stats */}
              {campusStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <p className="text-xs text-slate-400 uppercase">Total</p>
                    <p className="text-2xl font-semibold text-slate-900">{campusStats.total}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <p className="text-xs text-slate-400 uppercase">Con WhatsApp</p>
                    <p className="text-2xl font-semibold text-emerald-600">{campusStats.with_phone}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <p className="text-xs text-slate-400 uppercase">Solo Email</p>
                    <p className="text-2xl font-semibold text-slate-600">{campusStats.email_only ?? campusStats.without_phone ?? 0}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <p className="text-xs text-slate-400 uppercase">Notificados</p>
                    <p className="text-2xl font-semibold text-blue-600">{campusStats.notified}</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="relative flex-1 sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por email o nombre..."
                    value={waitlistSearch}
                    onChange={(e) => setWaitlistSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => fetchCampusWaitlist()} variant="outline" size="icon">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button onClick={() => exportWaitlist('campus')} variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Exportar CSV
                  </Button>
                </div>
              </div>

              {/* Table */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-100">
                      <tr>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Email</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Nombre</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Teléfono</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Fecha</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Estado</th>
                        <th className="text-right p-4 text-xs font-medium text-slate-500 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredCampusWaitlist.map(entry => (
                        <tr key={entry.id} className="hover:bg-slate-50">
                          <td className="p-4">
                            <p className="font-medium text-sm text-slate-900">{entry.email}</p>
                          </td>
                          <td className="p-4 text-sm text-slate-600">{entry.name || '-'}</td>
                          <td className="p-4 text-sm text-slate-600">
                            {entry.phone ? (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3 text-emerald-500" />
                                {entry.phone}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="p-4 text-sm text-slate-500">{formatDate(entry.subscribed_at)}</td>
                          <td className="p-4">
                            {entry.notified ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                                <Check className="w-3 h-3" /> Notificado
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                                Pendiente
                              </span>
                            )}
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex gap-1 justify-end">
                              {!entry.notified && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => markWaitlistNotified('campus', entry.id)}
                                  title="Marcar como notificado"
                                >
                                  <Bell className="w-4 h-4" />
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => deleteWaitlistEntry('campus', entry.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredCampusWaitlist.length === 0 && (
                        <tr>
                          <td colSpan={6} className="p-8 text-center text-slate-400">
                            No hay entradas en la lista de espera
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Publicaciones Waitlist */}
          {activeWaitlistTab === 'publicaciones' && (
            <div className="space-y-4">
              {/* Stats */}
              {publicacionesStats && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <p className="text-xs text-slate-400 uppercase">Total Interesados</p>
                    <p className="text-2xl font-semibold text-slate-900">{publicacionesStats.total}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <p className="text-xs text-slate-400 uppercase">Pendientes</p>
                    <p className="text-2xl font-semibold text-amber-600">{publicacionesStats.pending}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <p className="text-xs text-slate-400 uppercase">Notificados</p>
                    <p className="text-2xl font-semibold text-blue-600">{publicacionesStats.notified}</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="relative flex-1 sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por email..."
                    value={waitlistSearch}
                    onChange={(e) => setWaitlistSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => fetchPublicacionesWaitlist()} variant="outline" size="icon">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button onClick={() => exportWaitlist('publicaciones')} variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    Exportar CSV
                  </Button>
                </div>
              </div>

              {/* Table */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-100">
                      <tr>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Email</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Fecha</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Estado</th>
                        <th className="text-right p-4 text-xs font-medium text-slate-500 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredPublicacionesWaitlist.map(entry => (
                        <tr key={entry.id} className="hover:bg-slate-50">
                          <td className="p-4">
                            <p className="font-medium text-sm text-slate-900">{entry.email}</p>
                          </td>
                          <td className="p-4 text-sm text-slate-500">{formatDate(entry.subscribed_at)}</td>
                          <td className="p-4">
                            {entry.notified ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                                <Check className="w-3 h-3" /> Notificado
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                                Pendiente
                              </span>
                            )}
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex gap-1 justify-end">
                              {!entry.notified && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => markWaitlistNotified('publicaciones', entry.id)}
                                  title="Marcar como notificado"
                                >
                                  <Bell className="w-4 h-4" />
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => deleteWaitlistEntry('publicaciones', entry.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredPublicacionesWaitlist.length === 0 && (
                        <tr>
                          <td colSpan={4} className="p-8 text-center text-slate-400">
                            No hay entradas en la lista de espera
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Viajes Solicitudes */}
          {activeWaitlistTab === 'viajes' && (
            <div className="space-y-4">
              {/* Table */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-100">
                      <tr>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Email</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Nombre</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Teléfono</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Viaje</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Mensaje</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Fecha</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Estado</th>
                        <th className="text-right p-4 text-xs font-medium text-slate-500 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {viajesSolicitudes.map(entry => (
                        <tr key={entry.id} className="hover:bg-slate-50">
                          <td className="p-4">
                            <p className="font-medium text-sm text-slate-900">{entry.email}</p>
                          </td>
                          <td className="p-4 text-sm text-slate-600">{entry.name || '-'}</td>
                          <td className="p-4 text-sm text-slate-600">
                            {entry.phone ? (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3 text-emerald-500" />
                                {entry.phone}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="p-4">
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-teal-100 text-teal-700">
                              {entry.viaje}
                            </span>
                          </td>
                          <td className="p-4 text-sm text-slate-600 max-w-xs truncate" title={entry.mensaje}>
                            {entry.mensaje || '-'}
                          </td>
                          <td className="p-4 text-sm text-slate-500">{formatDate(entry.created_at)}</td>
                          <td className="p-4">
                            <select
                              value={entry.status || 'pending'}
                              onChange={(e) => updateViajeStatus(entry.id, e.target.value)}
                              className="px-2 py-1 text-xs font-medium rounded border border-slate-200 bg-white"
                            >
                              <option value="pending">Pendiente</option>
                              <option value="contacted">Contactado</option>
                              <option value="confirmed">Confirmado</option>
                              <option value="cancelled">Cancelado</option>
                            </select>
                          </td>
                          <td className="p-4 text-right">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => deleteViajeSolicitud(entry.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {viajesSolicitudes.length === 0 && (
                        <tr>
                          <td colSpan={8} className="p-8 text-center text-slate-400">
                            No hay solicitudes de viajes
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Límites Solicitudes */}
          {activeWaitlistTab === 'limites' && (
            <div className="space-y-4">
              {/* Table */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-100">
                      <tr>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Email</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Nombre</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Teléfono</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Respuesta 1</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Respuesta 2</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Respuesta 3</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Fecha</th>
                        <th className="text-left p-4 text-xs font-medium text-slate-500 uppercase">Estado</th>
                        <th className="text-right p-4 text-xs font-medium text-slate-500 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {limitesSolicitudes.map(entry => (
                        <tr key={entry.id} className="hover:bg-slate-50">
                          <td className="p-4">
                            <p className="font-medium text-sm text-slate-900">{entry.email}</p>
                          </td>
                          <td className="p-4 text-sm text-slate-600">{entry.name || '-'}</td>
                          <td className="p-4 text-sm text-slate-600">
                            {entry.phone ? (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3 text-emerald-500" />
                                {entry.phone}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="p-4 text-sm text-slate-600 max-w-xs truncate" title={entry.pregunta1}>
                            {entry.pregunta1 || '-'}
                          </td>
                          <td className="p-4 text-sm text-slate-600 max-w-xs truncate" title={entry.pregunta2}>
                            {entry.pregunta2 || '-'}
                          </td>
                          <td className="p-4 text-sm text-slate-600 max-w-xs truncate" title={entry.pregunta3}>
                            {entry.pregunta3 || '-'}
                          </td>
                          <td className="p-4 text-sm text-slate-500">{formatDate(entry.created_at)}</td>
                          <td className="p-4">
                            <select
                              value={entry.status || 'pending'}
                              onChange={(e) => updateLimitesStatus(entry.id, e.target.value)}
                              className="px-2 py-1 text-xs font-medium rounded border border-slate-200 bg-white"
                            >
                              <option value="pending">Pendiente</option>
                              <option value="contacted">Contactado</option>
                              <option value="enrolled">Inscrito</option>
                              <option value="rejected">Rechazado</option>
                            </select>
                          </td>
                          <td className="p-4 text-right">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => deleteLimitesSolicitud(entry.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {limitesSolicitudes.length === 0 && (
                        <tr>
                          <td colSpan={9} className="p-8 text-center text-slate-400">
                            No hay solicitudes del curso Límites
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== WEB STATS TAB ========== */}
      {activeTab === 'web' && (
        <div className="space-y-6" data-testid="web-tab">
          {!webStats ? (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
            </div>
          ) : (
            <>
              {/* Web KPIs */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-400 uppercase">Visitantes</span>
                    <Eye className="w-4 h-4 text-blue-500" />
                  </div>
                  <p className="text-2xl font-semibold text-slate-900">{webStats.unique_visitors}</p>
                  <p className="text-xs text-slate-400 mt-1">Últimos 30 días</p>
                </div>

                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-400 uppercase">Page Views</span>
                    <MousePointer className="w-4 h-4 text-purple-500" />
                  </div>
                  <p className="text-2xl font-semibold text-slate-900">{webStats.total_page_views}</p>
                </div>

                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-400 uppercase">Leads</span>
                    <UserPlus className="w-4 h-4 text-emerald-500" />
                  </div>
                  <p className="text-2xl font-semibold text-emerald-600">{webStats.leads_confirmed}</p>
                </div>

                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-400 uppercase">Conversión</span>
                    <Target className="w-4 h-4 text-amber-500" />
                  </div>
                  <p className="text-2xl font-semibold text-slate-900">{webStats.conversion_rates?.visitor_to_lead || 0}%</p>
                  <p className="text-xs text-slate-400 mt-1">Visitante → Lead</p>
                </div>
              </div>

              {/* By Landing */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-500" />
                  Visitas por Landing
                </h2>
                <div className="space-y-3">
                  {(webStats.by_landing || []).slice(0, 10).map((landing, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-600 truncate flex-1 font-medium">
                        /{landing.landing || 'home'}
                      </span>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <span className="text-sm font-semibold text-slate-900">{landing.views}</span>
                          <span className="text-xs text-slate-400 ml-1">views</span>
                        </div>
                        <div className="text-right min-w-[80px]">
                          <span className="text-sm text-slate-600">{landing.unique_visitors}</span>
                          <span className="text-xs text-slate-400 ml-1">únicos</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!webStats.by_landing || webStats.by_landing.length === 0) && (
                    <p className="text-sm text-slate-400 text-center py-4">Sin datos de tracking todavía</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ========== AUTOMATIONS TAB ========== */}
      {activeTab === 'automations' && <AdminAutomations />}

      {/* ========== FUNNELS TAB ========== */}
      {activeTab === 'funnels' && <AdminFunnels />}

      {/* ========== ATTRIBUTION / UTM TAB ========== */}
      {activeTab === 'attribution' && (
        <div className="space-y-6" data-testid="attribution-tab">
          {!webStats || !attributionStats ? (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
            </div>
          ) : (
            <>
              {/* Attribution KPIs */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-400 uppercase">Visitantes Tracked</span>
                    <Eye className="w-4 h-4 text-blue-500" />
                  </div>
                  <p className="text-2xl font-semibold text-slate-900">{attributionStats.total_tracked_visitors}</p>
                </div>

                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-400 uppercase">Convertidos</span>
                    <UserPlus className="w-4 h-4 text-emerald-500" />
                  </div>
                  <p className="text-2xl font-semibold text-emerald-600">{attributionStats.converted_visitors}</p>
                </div>

                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-400 uppercase">Conversión Global</span>
                    <Target className="w-4 h-4 text-purple-500" />
                  </div>
                  <p className="text-2xl font-semibold text-purple-600">{attributionStats.conversion_rate}%</p>
                </div>

                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-400 uppercase">Lead → Compra</span>
                    <ShoppingCart className="w-4 h-4 text-amber-500" />
                  </div>
                  <p className="text-2xl font-semibold text-slate-900">{webStats.conversion_rates?.lead_to_purchase || 0}%</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top UTM Sources */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                  <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-purple-500" />
                    Fuentes UTM (Últimos 30 días)
                  </h2>
                  <div className="space-y-3">
                    {(webStats.by_utm || []).slice(0, 8).map((utm, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{utm.source || 'directo'}</p>
                          <p className="text-xs text-slate-400">{utm.medium || '-'} / {utm.campaign || '-'}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-slate-900">{utm.visits}</span>
                          <span className="text-xs text-slate-400 ml-1">visitas</span>
                        </div>
                      </div>
                    ))}
                    {(!webStats.by_utm || webStats.by_utm.length === 0) && (
                      <p className="text-sm text-slate-400 text-center py-4">Sin tráfico con UTM todavía</p>
                    )}
                  </div>
                </div>

                {/* Top Converting Sources */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                  <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-emerald-500" />
                    Fuentes que Convierten
                  </h2>
                  <div className="space-y-3">
                    {(attributionStats.top_converting_sources || []).slice(0, 8).map((source, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                        <span className="text-sm font-medium text-slate-900">{source._id || 'directo'}</span>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-emerald-600">{source.conversions}</span>
                          <span className="text-xs text-slate-500 ml-1">conversiones</span>
                        </div>
                      </div>
                    ))}
                    {(!attributionStats.top_converting_sources || attributionStats.top_converting_sources.length === 0) && (
                      <p className="text-sm text-slate-400 text-center py-4">Sin conversiones atribuidas todavía</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Referrers */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <ExternalLink className="w-5 h-5 text-blue-500" />
                  Referrers (Enlaces entrantes)
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(webStats.by_referrer || []).slice(0, 10).map((ref, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-600 truncate flex-1 pr-4">{ref._id || 'Directo'}</span>
                      <span className="text-sm font-medium text-slate-900">{ref.visits}</span>
                    </div>
                  ))}
                  {(!webStats.by_referrer || webStats.by_referrer.length === 0) && (
                    <p className="text-sm text-slate-400 text-center py-4 col-span-2">Sin referrers registrados</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ========== NEW TAG MODAL ========== */}
      {showTagModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6 border-b border-slate-100">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-slate-900">Nuevo Tag</h3>
                <button onClick={() => setShowTagModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre *</label>
                <Input
                  value={newTag.name}
                  onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                  placeholder="ej: interes-nuevo-curso"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Color</label>
                <div className="flex gap-2">
                  {['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#6B7280'].map(color => (
                    <button
                      key={color}
                      onClick={() => setNewTag({ ...newTag, color })}
                      className={`w-8 h-8 rounded-full transition-all ${newTag.color === color ? 'ring-2 ring-offset-2 ring-slate-400' : ''}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Descripción</label>
                <Input
                  value={newTag.description}
                  onChange={(e) => setNewTag({ ...newTag, description: e.target.value })}
                  placeholder="Descripción opcional..."
                />
              </div>
            </div>
            <div className="p-6 border-t border-slate-100 flex gap-3">
              <Button variant="outline" onClick={() => setShowTagModal(false)} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={createTag} className="flex-1 bg-[#c9a227] hover:bg-[#b89220]">
                Crear tag
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ========== CONTACT DETAIL MODAL ========== */}
      {showContactModal && selectedContact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{selectedContact.email}</h3>
                  <p className="text-sm text-slate-500">
                    {selectedContact.first_name || ''} {selectedContact.last_name || ''}
                  </p>
                </div>
                <button onClick={() => setShowContactModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-400">Estado</p>
                  <p className="font-medium capitalize">{selectedContact.lifecycle_stage}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-400">Lead Score</p>
                  <p className="font-medium">{selectedContact.lead_score || 0}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-400">LTV</p>
                  <p className="font-medium text-emerald-600">{formatCurrency(selectedContact.ltv)}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-400">Fuente</p>
                  <p className="font-medium">{selectedContact.source || '-'}</p>
                </div>
              </div>

              {/* Historial 360 */}
              {selectedContact.history_360 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-slate-700">Historial 360</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-400">Detalle de origen</p>
                      <p className="font-medium text-sm">{selectedContact.history_360.source_detail || '-'}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-400">Consentimiento marketing</p>
                      <p className="font-medium text-sm">{selectedContact.history_360.consent_marketing ? 'Sí' : 'No'}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-400">Primer contacto</p>
                      <p className="font-medium text-sm">{formatDateTime(selectedContact.history_360.first_touch_at || selectedContact.created_at)}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-400">Última actividad</p>
                      <p className="font-medium text-sm">{formatDateTime(selectedContact.history_360.last_touch_at || selectedContact.updated_at)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Intereses */}
              {selectedContact.history_360?.interests && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Intereses</h4>
                  <div className="flex gap-2 flex-wrap">
                    {Object.entries(selectedContact.history_360.interests)
                      .filter(([, enabled]) => Boolean(enabled))
                      .map(([key]) => (
                        <span key={key} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                          {key}
                        </span>
                      ))}
                    {Object.entries(selectedContact.history_360.interests).filter(([, enabled]) => Boolean(enabled)).length === 0 && (
                      <span className="text-sm text-slate-400">Sin intereses marcados</span>
                    )}
                  </div>
                </div>
              )}

              {/* Solicitudes */}
              {selectedContact.history_360?.requests && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Solicitudes</h4>
                  <div className="space-y-2">
                    {Object.entries(selectedContact.history_360.requests)
                      .filter(([, req]) => Boolean(req))
                      .map(([key, req]) => (
                        <div key={key} className="p-3 bg-amber-50 rounded-lg">
                          <p className="text-sm font-medium capitalize text-amber-700">{key}</p>
                          <p className="text-xs text-slate-600 mt-1">
                            {typeof req === 'object' ? JSON.stringify(req) : String(req)}
                          </p>
                        </div>
                      ))}
                    {Object.entries(selectedContact.history_360.requests).filter(([, req]) => Boolean(req)).length === 0 && (
                      <span className="text-sm text-slate-400">Sin solicitudes</span>
                    )}
                  </div>
                </div>
              )}

              {/* Tags */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Tags</h4>
                <div className="flex gap-2 flex-wrap">
                  {(selectedContact.tag_names || []).map((tag, i) => (
                    <span key={i} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                      {tag}
                    </span>
                  ))}
                  {(selectedContact.tag_names || []).length === 0 && (
                    <span className="text-sm text-slate-400">Sin tags</span>
                  )}
                </div>
              </div>

              {/* Purchases */}
              {selectedContact.purchases && selectedContact.purchases.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Compras ({selectedContact.purchases.length})</h4>
                  <div className="space-y-2">
                    {selectedContact.purchases.map((p, i) => (
                      <div key={i} className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{p.product_name || p.product_type}</p>
                          <p className="text-xs text-slate-500">{p.provider} - {formatDate(p.created_at)}</p>
                        </div>
                        <span className="font-semibold text-emerald-600">{formatCurrency(p.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Events */}
              {selectedContact.timeline && selectedContact.timeline.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Timeline (últimos eventos)</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedContact.timeline.slice(0, 15).map((e, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm p-2 bg-slate-50 rounded-lg">
                        <Activity className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{e.event_name || e.event_type || 'evento'}</span>
                        {e.source && (
                          <span className="px-2 py-0.5 text-[10px] uppercase bg-slate-200 text-slate-600 rounded">
                            {e.source}
                          </span>
                        )}
                        <span className="text-slate-400 text-xs ml-auto">
                          {formatDateTime(e.event_time)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMarketing;
